import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../hooks/useTranslation';
import { promotionsService } from '../../services/promotionsService';
import { Promotion, PromotionStatus, PromotionDiscountType } from 'public-contracts';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Loader2 } from 'lucide-react';

interface PromotionSelectorProps {
  selectedPromotionIds: string[];
  onChange: (promotionIds: string[]) => void;
  orderType?: string;
  disabled?: boolean;
}

export const PromotionSelector: React.FC<PromotionSelectorProps> = ({
  selectedPromotionIds,
  onChange,
  orderType,
  disabled = false,
}) => {
  const { translate } = useTranslation();
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPromotions = async () => {
      try {
        setLoading(true);
        setError(null);
        const activePromotions = await promotionsService.getActivePromotions(orderType);
        setPromotions(activePromotions);
      } catch (err) {
        console.error('Error loading promotions:', err);
        setError(err instanceof Error ? err.message : 'Failed to load promotions');
      } finally {
        setLoading(false);
      }
    };

    loadPromotions();
  }, [orderType]);

  const handleTogglePromotion = (promotionId: string) => {
    if (disabled) return;

    const isSelected = selectedPromotionIds.includes(promotionId);
    const promotion = promotions.find((p) => p.id === promotionId);

    if (isSelected) {
      // Remove promotion
      onChange(selectedPromotionIds.filter((id) => id !== promotionId));
    } else {
      // Check if promotion is stackable or if no other promotions are selected
      if (promotion && !promotion.isStackable && selectedPromotionIds.length > 0) {
        // Non-stackable promotion - replace existing selections
        onChange([promotionId]);
      } else {
        // Add promotion (stackable or first selection)
        onChange([...selectedPromotionIds, promotionId]);
      }
    }
  };

  const getPromotionDisplayText = (promotion: Promotion): string => {
    const discountText =
      promotion.discountType === PromotionDiscountType.PERCENTAGE
        ? `${promotion.discountValue}%`
        : `${promotion.discountValue}`;
    return `${promotion.name} (${discountText} off)`;
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {translate('pos.orders.fields.promotion')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span className="ml-2 text-sm text-gray-500">
              {translate('pos.orders.messages.loadingPromotions', 'Loading promotions...')}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {translate('pos.orders.fields.promotion')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-red-600">{error}</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {translate('pos.orders.fields.promotion')}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {promotions.length === 0 ? (
          <div className="text-sm text-gray-500">
            {translate(
              'pos.orders.messages.noPromotionsAvailable',
              'No active promotions available'
            )}
          </div>
        ) : (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {promotions.map((promotion) => {
              const isSelected = selectedPromotionIds.includes(promotion.id);
              const isDisabled =
                disabled ||
                (!promotion.isStackable &&
                  selectedPromotionIds.length > 0 &&
                  !isSelected);

              return (
                <div
                  key={promotion.id}
                  className={`flex items-start space-x-3 p-2 rounded-md border ${
                    isSelected ? 'bg-blue-50 border-blue-200' : 'border-gray-200'
                  } ${isDisabled ? 'opacity-50' : ''}`}
                >
                  <Checkbox
                    id={`promotion-${promotion.id}`}
                    checked={isSelected}
                    onCheckedChange={() => handleTogglePromotion(promotion.id)}
                    disabled={isDisabled}
                    className="mt-1"
                  />
                  <div className="flex-1 min-w-0">
                    <Label
                      htmlFor={`promotion-${promotion.id}`}
                      className="font-medium cursor-pointer"
                    >
                      {promotion.name}
                    </Label>
                    <div className="text-sm text-gray-600 mt-1">
                      {getPromotionDisplayText(promotion)}
                      {promotion.description && (
                        <span className="block text-xs text-gray-500 mt-1">
                          {promotion.description}
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2 mt-2">
                      {!promotion.isStackable && (
                        <Badge variant="outline" className="text-xs">
                          {translate(
                            'pos.orders.promotion.notStackable',
                            'Not stackable'
                          )}
                        </Badge>
                      )}
                      {promotion.minimumOrderAmount && (
                        <Badge variant="outline" className="text-xs">
                          Min: {promotion.minimumOrderAmount}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {selectedPromotionIds.length > 0 && (
          <div className="pt-2 border-t">
            <div className="text-sm font-medium mb-2">
              {translate('pos.orders.promotion.selected', 'Selected:')}
            </div>
            <div className="flex flex-wrap gap-2">
              {selectedPromotionIds.map((id) => {
                const promotion = promotions.find((p) => p.id === id);
                if (!promotion) return null;
                return (
                  <Badge key={id} variant="secondary">
                    {promotion.name}
                  </Badge>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

